/**
 * Exact money arithmetic for the device.
 *
 * The server stores NUMERIC(19,4) and sends decimal strings. The phone must never add them as
 * JavaScript numbers (0.1 + 0.2 !== 0.3), so amounts are held as BigInt "units" of 1/10 000 -
 * the server's own precision - and formatted back with the server's rules: at least two
 * decimals, more only when they are non-zero (1500 -> "1500.00", 3.3333 -> "3.3333").
 *
 * Only display and provisional (unsynced) figures use this. The server's own numbers remain the
 * source of truth and replace anything computed here as soon as a sync lands.
 */

const FACTOR = 10_000n;
const PATTERN = /^-?\d+(\.\d{1,4})?$/;

export class MoneyParseError extends Error {}

/** "1,250.5" style input is a UI concern; this accepts the server's canonical form only. */
export function toUnits(value: string): bigint {
  const text = value.trim();
  if (!PATTERN.test(text)) throw new MoneyParseError(`not a valid amount: ${JSON.stringify(value)}`);
  const negative = text.startsWith("-");
  const [whole = "0", frac = ""] = text.replace("-", "").split(".");
  const units = BigInt(whole) * FACTOR + BigInt(frac.padEnd(4, "0"));
  return negative ? -units : units;
}

export function fromUnits(units: bigint): string {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const whole = abs / FACTOR;
  let frac = (abs % FACTOR).toString().padStart(4, "0");
  while (frac.length > 2 && frac.endsWith("0")) frac = frac.slice(0, -1);
  return `${negative && abs !== 0n ? "-" : ""}${whole}.${frac}`;
}

export const addMoney = (a: string, b: string): string => fromUnits(toUnits(a) + toUnits(b));
export const subtractMoney = (a: string, b: string): string => fromUnits(toUnits(a) - toUnits(b));
export const negateMoney = (a: string): string => fromUnits(-toUnits(a));

export function sumMoney(values: readonly string[]): string {
  return fromUnits(values.reduce((total, v) => total + toUnits(v), 0n));
}

export function compareMoney(a: string, b: string): -1 | 0 | 1 {
  const x = toUnits(a);
  const y = toUnits(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

export function isPositiveMoney(a: string): boolean {
  return toUnits(a) > 0n;
}
